<template>
    <div class="comparison-table">
        <!-- Análise financeira para tabelas de vendas do tipo Vertical/Incorporadora -->
        <div v-if="enableVerticalSimulator &&  product?.availability?.type === 'vertical' && salesTable?.simulationType === 'vertical'" class="comparison-table-vertical">
            <ui-simulation-financial-analysis :data="simulation" />
            <ui-simulation-capture-analysis :series="series" :staticSeries="staticSeries" :monthly="true"/>
            <ui-simulation-capture-analysis :series="series" :staticSeries="staticSeries" :monthly="false"/>
        </div>

        <div v-else class="comparison-table-default">
            <!-- A tabela de comparativo só é visualizada se houver um fluxo selecionado -->
            <ui-comparison-table
                v-if="salesTableFlow"
                :table="comparativeTableComputed"
                title="Comparativo - Tabela x Proposta"
            />
            <ui-comparison-table :table="paymentFlowTableComputed" title="Fluxo de Pagamento Proposta" />
        </div>
    </div>
</template>

<script>
    import uiComparisonTable from '@/components/ui-facilita/sales/comparison-table'
    import { uiSimulationFinancialAnalysis, uiSimulationCaptureAnalysis } from '@/components/ui-facilita/sales/simulation'
    import salesTable from '@/mixins/calculations/salesTable'
    import { mapGetters } from 'vuex'
    import { storage } from '@/services/storage'

    function currencyBRL(num) {
        const formated = new Intl.NumberFormat('pt-Br', {
            style: 'currency',
            currency: 'BRL',
        }).format(num)

        return formated
    }

    export default {
        name: 'ui-simulation-comparison',

        components: {
            uiComparisonTable,
            uiSimulationFinancialAnalysis,
            uiSimulationCaptureAnalysis,
        },

        mixins: [salesTable],
        data(){
            return {
                isFirstTime:true,
                enableVerticalSimulator: false,
                rowInfo: {},
            }
        },
        created(){
		    this.enableVerticalSimulator = Boolean(parseInt(JSON.parse(storage.get('get-configs-data')).enableVerticalSimulator))
            this.isFirstTime = true
        },
        mounted(){
            this.isFirstTime = true
            this.$root.$on('parcel-has-updated', ({ data, parcelHasChanged }) => {
            this.rowInfo = {...data}
            this.rowInfo.parcelHasChanged = parcelHasChanged
        });
        },
        beforeDestroy() {
            this.$root.$off('parcel-has-updated')
        },
        computed: {
            ...mapGetters('simulation', {
                client: 'getCustomer',
                clientDeal: 'getCustomerDeal',
                defaultSimulation: 'getDefaultSimulation',
                fixedValue: 'getFixedValue',
                owner: 'getSimulationOwner',
                proposedBaseCost: 'getProposedBaseCost',
                proposedFutureValue: 'getProposedFutureValue',
                proposedValue: 'getProposedValue',
                product: 'getProduct',
                salesTableFlow: 'getSalesTableFlow',
                series: 'getSeries',
                unityValueWithDiscount: 'getUnityValueWithDiscount',
                simulation: 'getSimulation',
                staticSeries:'getStaticSeries',
                salesTable:'getSalesTable'
            }),
            /**
             * Computed property que gera os dados referentes a tabela de análise financeira do comparativo
             * entre o fluxo de pagamento da tabela e o fluxo da simulação
             */
            comparativeTableComputed() { 
                return {
                    thead: ['Dados','Tabela','Proposta','Variação'],
                    tbody: [
                        [
                            'Preço do m²',
                            currencyBRL(this.defaultSimulation.baseCost),
                            currencyBRL(this.proposedBaseCost),
                            this.calcDiff(this.defaultSimulation.baseCost, this.proposedBaseCost)
                        ],
                        [
                            'Valor Presente',
                            currencyBRL(this.defaultSimulation.presentValue),
                            currencyBRL(this.proposedValue),
                            this.calcDiff(this.defaultSimulation.presentValue, this.proposedValue)
                        ],
                        [
                            'Valor Futuro',
                            currencyBRL(this.defaultSimulation.futureValue),
                            currencyBRL(this.proposedFutureValue),
                            this.calcDiff(this.defaultSimulation.futureValue, this.proposedFutureValue)
                        ],
                    ]
                }
            },
            /**
             * Computed property que gera os dados referentes a tabela de análise financeira do fluxo
             * de pagamento da simulação
             */
            paymentFlowTableComputed() {
                /** Processa as séries/parcelas da simulação, de modo a calcular seus valores presente, futuro e juros */
                let unityValue = this.unityValueWithDiscount - this.fixedValue
                let seriesFlows = this.series.map(series => {

                    let pv = (parseFloat(series.percent) * unityValue) / 100
                    let fv = parseFloat(series.first_value) * series.quantity
                    /** Para parcelas do tipo calcula o valor futuro */
                    if (series.type_installment === 'fixed_value' || series.consider === 'unity-discount') pv = fv

                    let serieName = series.display_name;
                  
                    if(typeof serieName == 'undefined' && this.rowInfo?.id  !== series.id){
                        serieName = this.facGetFrequency(series.type_installment).display_name
                    }
                    if(parseInt(this.rowInfo?.id)  == parseInt(series.id)){
                        serieName = this.facGetFrequency(series.type_installment).display_name
                    }
                    if(this.rowInfo.parcelHasChanged && parseInt(this.rowInfo?.id)  !== parseInt(series.id)){
                        serieName = this.facGetFrequency(series.type_installment).display_name
                    }
                    return [
                        serieName,
                        currencyBRL(pv),
                        currencyBRL(fv-pv),
                        `<span style="color: #0181e2;">${currencyBRL(fv)}</span>`
                    ]
                })
                return {
                    thead: ['Parcela','Valor Presente','Juros','Valor Futuro'],
                    tbody: [
                        ...seriesFlows,
                        [
                            '<span style="font-size: 15px;">Total</span>',
                            `<strong style="color: #0181e2; font-size: 14px;">${currencyBRL(this.proposedValue)}</strong>`,
                            `<strong style="color: #0181e2; font-size: 14px;">${currencyBRL(this.proposedFutureValue - this.proposedValue)}</strong>`,
                            `<strong style="color: #12aa6e; font-size: 14px;">${currencyBRL(this.proposedFutureValue)}</strong>`
                        ],
                    ]
                }
            }
        },

        methods: {
            /**
             * Calcula a diferença entre dois valores (percentual e valor bruto) retorna um layout customizado
             */
             calcDiff(value1, value2) {
                let result = value2 - value1;

                let percent = (parseInt(value1) !== 0)
                    ? ((value2 - value1) / value1) * 100
                    : 0;

                result = !isNaN(result) ? result : 0;
                percent = !isNaN(percent) ? percent : 0;

                return result >= 0
                    ? `<div style="color: #12aa6e;"><span>${currencyBRL(result)}</span><span>${percent.toFixed(2).replace('.', ',')}%</span></div>`
                    : `<div style="color: #f53b57;"><span>- ${currencyBRL(Math.abs(result))}</span><span>${percent.toFixed(2).replace('.', ',')}%</span></div>`;
            }
        },
    }
</script>
    <style lang="stylus" scoped>
</style>